import { FormEvent, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/auth";
import { useAuth } from "../../../providers/AuthProvider";
import { useThemeMode } from "../../../providers/ThemeProvider";
import { storage } from "../../shared/api/storage";
import { AUTH_REDIRECT_KEY } from "../../../providers/AuthProvider";
import { getActiveLocale } from "../../../i18n";
import { AuthFormBase } from "../components/AuthFormBase";
import { UserPlus } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { getNextPathFromSearch } from "../../../utils/navigation";

const primaryButtonClasses = "pm-btn pm-btn-primary w-full";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const fieldClasses = "flex flex-col gap-2";

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const { theme } = useThemeMode();
  const nextFromQuery = useMemo(() => getNextPathFromSearch(location.search), [location.search]);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const trimmedFullName = fullName.trim();
    const derivedDisplayName = trimmedFullName.split(/\s+/).filter(Boolean)[0] ?? "";

    if (!trimmedFullName) {
      setError(t("auth.register.name_required"));
      return;
    }
    if (trimmedFullName.length < 3) {
      setError(t("auth.register.full_name_min"));
      return;
    }
    if (derivedDisplayName.length < 2) {
      setError(t("auth.register.display_name_min"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("auth.register.passwords_mismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      if (nextFromQuery) {
        storage.set(AUTH_REDIRECT_KEY, nextFromQuery);
      }
      const preferredLocale = getActiveLocale();
      await register({
        full_name: trimmedFullName,
        name: derivedDisplayName,
        email,
        password,
        preferred_locale: preferredLocale,
        preferences: {
          appearance: {
            theme: theme === "dark" ? "dark" : "light",
          },
        },
      });
      navigate(nextFromQuery ?? "/dashboard", { replace: true });
      if (nextFromQuery) {
        storage.remove(AUTH_REDIRECT_KEY);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.body?.message ?? t("auth.register.creation_error"));
      } else {
        setError(t("common.error.unexpected"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormBase
      onSubmit={handleSubmit}
      error={error}
      submitButton={
        <button
          type="submit"
          className={primaryButtonClasses}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            t("auth.register.creating")
          ) : (
            <>
              <UserPlus {...defaultIconProps} aria-hidden />
              {t("auth.register.button")}
            </>
          )}
        </button>
      }
      socialDisabled={isSubmitting}
      socialButtonText={{ google: t("auth.signup.google_button") }}
    >
      <div className={fieldClasses}>
        <label htmlFor="register-name">
          <FormFieldLabel label={t("forms.name.full_label")} />
        </label>
        <input
          id="register-name"
          type="text"
          className={inputClasses}
          autoComplete="name"
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
          placeholder={t("forms.name.full_name_placeholder")}
          required
        />
      </div>

      <div className={fieldClasses}>
        <label htmlFor="register-email">
          <FormFieldLabel label={t("forms.email.label")} />
        </label>
        <input
          id="register-email"
          type="email"
          inputMode="email"
          className={inputClasses}
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </div>

      <div className={fieldClasses}>
        <label htmlFor="register-password">
          <FormFieldLabel label={t("forms.password.label")} />
        </label>
        <input
          id="register-password"
          type="password"
          className={inputClasses}
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          minLength={8}
          required
        />
      </div>

      <div className={fieldClasses}>
        <label htmlFor="register-confirm-password">
          <FormFieldLabel label={t("forms.password.confirm_label")} />
        </label>
        <input
          id="register-confirm-password"
          type="password"
          className={inputClasses}
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          minLength={8}
          required
        />
      </div>
    </AuthFormBase>
  );
}
